import capitalize from 'lodash/capitalize';

import { Badge, Box, Flex, Text, useColorModeValue } from '@vimeo/bokeh';
import { CircleExclamationFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { type PaywallTrackingProps } from '@vimeo/picox';
import {
  type SubscriptionPlans,
  useGetSubscriptionPlansData,
} from '@vimeo/subscription-api-plans-data-hook';

import { OtherPlansButton, PaywallCTAsWrapper, PurchaseButton } from '../components';
import { useCurrentStorageLimit } from '../hooks';

interface StorageLimitPaywallProps {
  isUserEligibleForFreeTrial?: boolean;
  tier?: Partial<SubscriptionPlans['tier']>;
  paywallTracking: PaywallTrackingProps;
}

export function StorageLimitPaywall({
  tier = 'standard',
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: StorageLimitPaywallProps) {
  const targetPlans = useGetSubscriptionPlansData([tier], undefined, false);
  const { currentStorageLimit, currentRestrictedStorageLimit } = useCurrentStorageLimit();
  const targetPlan = targetPlans?.find((plan) => plan.tier === tier) ?? targetPlans?.[0];
  const targetRestrictedStorageLimit =
    targetPlan?.metadata?.entitlements?.params?.restrictedVideoStorageLimit;
  const hasRestrictedTargetStorageLimit = targetRestrictedStorageLimit != null;

  const targetStorageLimit =
    targetRestrictedStorageLimit ??
    targetPlan?.metadata?.entitlements?.params?.videoStoragePeriodicQuota ??
    '';
  const resolvedStorageLimit = currentRestrictedStorageLimit || currentStorageLimit;
  const resolvedPlanName = targetPlan?.name ?? (tier ? capitalize(`${tier}`) : '');

  const storageImageUrl = useColorModeValue(
    'https://i.vimeocdn.com/custom_asset/c3e7bb4028bc2edb60f54310fadd1000',
    'https://i.vimeocdn.com/custom_asset/e9c789e68e6e64c62a6fe8d321438a92',
  );

  const useTrialCheckout = Boolean(targetPlan?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = useTrialCheckout && isUserEligibleForFreeTrial;

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      height={{ base: '100%', md: 'auto' }}
      maxHeight={{ base: 'none', md: 'calc(100vh - 120px)' }}
      position="relative"
    >
      <Flex
        direction="column"
        alignItems="center"
        width="100%"
        maxWidth="400px"
        maxHeight="100%"
        minHeight="0"
        gap="16px"
        flex="1 1 auto"
        pt={{ base: '24px', md: '0' }}
        pb="24px"
        mx={{ base: 'auto', md: '32px' }}
        overflowY="auto"
      >
        <Box
          width="192px"
          height="140px"
          minHeight="93px"
          flex="1 1 140px"
          backgroundImage={`url(${storageImageUrl})`}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          marginY="16px"
        />

        <Flex direction="column" alignItems="center" gap="16px" width="100%" paddingX="32px">
          <Text
            variant={{ base: 'heading-xl', md: 'heading-2xl' }}
            textAlign="center"
            flexShrink={0}
          >
            {translate('You’ve reached your limit')}
          </Text>
          <Text variant="heading-sm" color="text-primary" textAlign="center" flexShrink={0}>
            {hasRestrictedTargetStorageLimit
              ? translate({
                  singular:
                    'You’ve reached the limit for embeddable and non-public videos on your current plan. Upgrade to {PLAN_NAME} for more storage.',
                  replacements: { PLAN_NAME: resolvedPlanName },
                })
              : translate({
                  singular:
                    'You’ve reached the {STORAGE_LIMIT} storage limit for this plan. Upgrade to {PLAN_NAME} to use more space.',
                  replacements: {
                    STORAGE_LIMIT: resolvedStorageLimit,
                    PLAN_NAME: resolvedPlanName,
                  },
                })}
          </Text>
        </Flex>

        <Box
          border="1px solid"
          borderColor="upsell-primary"
          borderRadius="20px"
          px="40px"
          py="32px"
          width="100%"
          position="relative"
          marginTop="24px"
        >
          <Flex alignItems="center" justifyContent="space-between" mb="16px">
            <Flex alignItems="center" gap="4px">
              <Text variant="heading-2xs" color="text-tertiary">
                {translate('Your plan')}
              </Text>
              <Badge variant="neutral" size="sm">
                {resolvedStorageLimit}
              </Badge>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <Text variant="heading-2xs" color="upsell-primary">
                {resolvedPlanName}
              </Text>
              <Badge variant="upgrade" size="sm">
                {targetStorageLimit}
              </Badge>
            </Flex>
          </Flex>

          <Box
            width="100%"
            height="4px"
            bg="text-tertiary"
            borderRadius="9999px"
            position="relative"
          >
            <Box width="78%" ml="auto" height="4px" bg="upsell-primary" borderRadius="9999px" />
            <Box
              position="absolute"
              left="22%"
              top="-6px"
              width="0px"
              height="16px"
              borderLeft="2px dotted"
              borderColor="text-tertiary"
            />
          </Box>

          <CircleExclamationFilled
            color="upsell-primary"
            width="40px"
            height="40px"
            transform="rotate(15deg)"
            position="absolute"
            right="9.01px"
            top="-24px"
          />
        </Box>
      </Flex>

      <PaywallCTAsWrapper>
        <PurchaseButton
          subscriptionTier={tier}
          isTrial={isTrial}
          planName={resolvedPlanName}
          paywallTracking={paywallTracking}
        />
        <OtherPlansButton paywallTracking={paywallTracking} />
      </PaywallCTAsWrapper>
    </Flex>
  );
}
